import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScoreBreakdown } from '../../types';

interface ScoreSectionProps {
  globalScore: number;
  scoreBreakdown: ScoreBreakdown;
}

const ScoreSection: React.FC<ScoreSectionProps> = ({ globalScore, scoreBreakdown }) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const { t } = useTranslation();
  
  useEffect(() => {
    let currentScore = 0;
    const interval = setInterval(() => {
      currentScore += 2;
      if (currentScore >= globalScore) {
        currentScore = globalScore;
        clearInterval(interval);
      }
      setAnimatedScore(currentScore);
    }, 30);
    
    return () => clearInterval(interval);
  }, [globalScore]);
  
  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#ff9800';
    if (score >= 40) return '#ff5722';
    return '#f44336';
  };
  
  const getScoreDescription = (score: number): string => {
    if (score >= 80) return t('score.excellent');
    if (score >= 60) return t('score.good');
    if (score >= 40) return t('score.moderate');
    return t('score.weak');
  };
  
  const sectionNames: Record<string, string> = {
    skills: t('score.sections.skills'),
    experience: t('score.sections.experience'),
    education: t('score.sections.education'),
    certifications: t('score.sections.certifications'),
    languages: t('score.sections.languages')
  };
  
  const sectionColors: Record<string, string> = {
    skills: '#2196f3',
    experience: '#ff9800',
    education: '#4caf50',
    certifications: '#9c27b0',
    languages: '#00bcd4'
  };
  
  return (
    <div className="text-center p-8 bg-white rounded-xl shadow-md">
      <h2 className="text-2xl font-bold mb-6">{t('score.global_compatibility')}</h2>
      
      <div className="relative w-36 h-36 mx-auto mb-6">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="10"
          />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={getScoreColor(globalScore)}
            strokeWidth="10"
            strokeDasharray={`${animatedScore * 2.83} ${283 - animatedScore * 2.83}`}
            strokeDashoffset="70.75"
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-3xl font-bold">{animatedScore}%</span>
        </div>
      </div>
      
      <h3 className="text-xl font-medium mb-8">{getScoreDescription(globalScore)}</h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
        {Object.entries(scoreBreakdown)?.map(([section, data]) => (
          <div key={section} className="bg-gray-50 p-4 rounded-lg text-center shadow-sm">
            <div className="text-2xl font-bold mb-2" style={{ color: sectionColors[section] || '#666' }}>
              {data?.score}%
            </div>
            <div className="text-gray-800 font-medium">{sectionNames[section] || section}</div>
            <div className="text-xs text-gray-500 mt-1">{t('score.weight')}: {Math.round(data?.weight * 100)}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScoreSection;